<!--
	@name app-calculator-max-ltv-tool
	@description Max LTV Tool
	@date 2026/08/05
	@license no license
	@copywrite Answers In Retirement Limited
-->

<template>
	<div :component="$options.name">
		<v-dialog v-model="maxLtvToolActive" fullscreen hide-overlay transition="dialog-bottom-transition">
			<v-card tile>
				<v-toolbar flat dark color="info" class="pl-3 pr-3 flex-grow-0 generic-gradient">
					<v-row>
						<v-col cols="12" md="2" class="d-flex pa-0">
							<v-toolbar-title class="align-self-center">
								Max LTV Tool
							</v-toolbar-title>
						</v-col>
						<v-spacer />
						<v-col cols="12" md="2" class="d-flex pa-0 justify-end">
							<v-btn icon dark class="align-self-center" @click="close()">
								<v-icon>mdi-close</v-icon>
							</v-btn>
						</v-col>
					</v-row>
				</v-toolbar>
				<v-container fluid class="pa-6 pt-10 ma-0 mx-auto" style="max-width: 1400px">
					<common-structure-section no-top-radius :body-class="'flex-grow-1'">
						<template #body>
							<div :component="$options.name">
								<p class="text-body-1 mb-5">
									This tool gives a quick estimate of how much your customer(s) could borrow and the rates available. Select single or joint, enter the youngest
									applicant's age and property value, then use the sliders to explore available loan options and rates.
								</p>

								<v-sheet outlined rounded class="py-2 px-6">
									<v-row>
										<v-col class="shrink" style="align-content: start; width: 200px">
											<p class="text-body-1 font-weight-medium mb-4 nowrap">
												Single / Joint:
											</p>
											<v-btn-toggle v-model="applicationType" color="primary" mandatory @change="lifeTypeUpdate">
												<v-btn v-for="(option, index) in applicationTypes" :key="index" :value="option.value">
													<v-icon>{{ option.icon }}</v-icon>
												</v-btn>
											</v-btn-toggle>
										</v-col>
										<v-col class="shrink">
											<v-divider vertical />
										</v-col>
										<v-col class="grow" style="align-content: start">
											<p class="text-body-1 font-weight-medium mb-9 line-clamp line-clamp--1">
												Age of Youngest Applicant:
											</p>
											<v-slider
												v-model="age"
												min="50"
												max="90"
												hide-details=""
												thumb-label="always"
												thumb-size="26"
												class="mt-0"
												@end="loading = true"
												@change="ageUpdate"
											>
												<template #thumb-label="{ value }">
													{{ value }}{{ age === 90 ? '+' : null }}
												</template>

												<template #prepend>
													<p class="text-body-2 grey--text mb-0">
														50
													</p>
												</template>

												<template #append>
													<p class="text-body-2 grey--text mb-0">
														90+
													</p>
												</template>
											</v-slider>
										</v-col>
										<v-col class="shrink">
											<v-divider vertical />
										</v-col>
										<v-col class="shrink" style="align-content: start">
											<p class="text-body-1 font-weight-medium mb-2 nowrap">
												Property Valuation:
											</p>
											<validation-observer ref="age_observer">
												<validation-provider v-slot="{ errors }" name="Property Value (£)" rules="required|numeric|min_value:50000|max_value:10000000">
													<v-text-field
														v-model="valuation"
														outlined
														:hide-details="!errors.length"
														:error-messages="errors"
														style="width: 250px"
														prepend-inner-icon="mdi-currency-gbp"
														@input="valuationUpdate"
													>
														<template #prepend-inner>
															<v-icon size="22">
																mdi-currency-gbp
															</v-icon>
														</template>
													</v-text-field>
												</validation-provider>
											</validation-observer>
										</v-col>
									</v-row>
								</v-sheet>

								<v-divider inset class="my-10" />

								<div v-if="loading" class="d-flex flex-wrap justify-center">
									<v-progress-circular indeterminate />
								</div>
								<v-fade-transition hide-on-leave>
									<div v-if="!loading" class="d-flex flex-wrap ma-n4">
										<v-card v-for="card in cards" :key="card.ref" class="d-flex flex-column ma-4" style="flex-basis: calc(50% - 32px)">
											<v-card-title
												class="secondary white--text font-weight-bold text-body-1 py-3 cursor--pointer"
												@click="openProductDescription(card.description)"
											>
												<div class="line-clamp line-clamp--1">
													{{ card.title }}
													<v-icon small class="ml-1" color="white" style="position: relative; top: -4px">
														mdi-help-box
													</v-icon>
												</div>
											</v-card-title>
											<v-card-text class="pa-0">
												<div class="d-flex">
													<div class="d-flex flex-column flex-grow-1">
														<div class="pa-4" style="border-bottom: 1px solid #dadada !important">
															<p class="text-body-1 grey--text text--darken-3 mb-0">
																Rates from <span class="font-weight-bold secondary--text">{{ card.minRate | numFormat('0.00') }}%</span> to
																<span class="font-weight-bold secondary--text">{{ card.maxRate | numFormat('0.00') }}%</span>, with a Max LTV of
																<span class="font-weight-bold secondary--text">{{ card.maxLtv | numFormat('0,0.0') }}%</span>. Use the slider to
																explore the rate at different LTVs.
															</p>
														</div>
														<div class="px-6 py-4">
															<v-slider
																v-model="card.amount"
																hide-details
																min="0"
																:max="calcLoan(card.maxLtv)"
																step="1000"
																color="grey darken-2"
																track-color="grey lighten-2"
																class="ma-0"
																@input="ltvUpdate(card)"
															>
																<template #prepend>
																	<p class="text-body-2 grey--text text--darken-2 mb-0">
																		£0
																	</p>
																</template>

																<template #append>
																	<p class="text-body-2 grey--text text--darken-2 mb-0">
																		£{{ calcLoan(card.maxLtv) | numFormat('0,0') }}
																	</p>
																</template>
															</v-slider>
														</div>
														<div class="d-flex flex-grow-1 blue-grey lighten-5">
															<div class="logo flex-grow-1 text-center pa-3">
																<p class="text-body-2 font-weight-light mb-0">
																	Loan
																</p>
																<p class="text-h5 font-weight-bold grey--text text--darken-3 mb-0">
																	£{{ card.amount | numFormat('0,0') }}
																</p>
															</div>
															<div class="logo flex-grow-1 text-center pa-3">
																<p class="text-body-2 font-weight-light mb-0">
																	{{ card.rateType }}
																</p>
																<v-progress-circular v-if="card.rate === undefined" indeterminate size="24" color="grey darken-3" class="mt-1" />
																<p v-else class="text-h5 font-weight-bold grey--text text--darken-3 mb-0">
																	{{ card.rate | numFormat('0.00') }}%
																</p>
															</div>
														</div>
													</div>
												</div>
											</v-card-text>
										</v-card>
									</div>
								</v-fade-transition>

								<p class="text-caption plain mt-6 mb-0">
									<a @click="openDisclaimer">Click here</a> to learn more about the data intelligence behind the tool.
								</p>

								<common-dialog ref="disclaimerDialog">
									<template #header>
										Max LTV Tool
									</template>
									<template #body>
										<v-sheet>
											<p class="text-body-2 mb-3">
												The Max LTV tool is supported by market data spanning the previous 72 hours and captures the best rate for each LTV band for every combination of product type and age, for both joint and single-life cases.
											</p>
											<p class="text-body-2 mb-3">
												Where joint-life is selected, calculations are based on the youngest applicant's age. Where products are assessed on a joint-life basis and maximum age may impact eligibility, a full source with client details is required to confirm accuracy. Figures assume a standard freehold detached property in England.
											</p>
											<p class="text-body-2 mb-3">
												Search results display products from across the whole market.
											</p>
											<p class="text-body-2 mb-0">
												Please note that the rates shown are based on the limited information entered. Some rates and LTVs displayed in the search results may not be available in a full source once all criteria have been considered, as lender-specific underwriting requirements can affect eligibility. As a result, rates and product availability may vary.
											</p>
										</v-sheet>
									</template>
								</common-dialog>

								<common-dialog ref="productDescriptionDialog">
									<template #header>
										{{ productDescription.title }}
									</template>
									<template #body>
										<v-sheet>
											<p class="text-body-2 mb-0">
												{{ productDescription.description }}
											</p>
										</v-sheet>
									</template>
								</common-dialog>
							</div>
						</template>
					</common-structure-section>
				</v-container>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
	import { mapActions } from 'vuex';
	import numeral from 'numeral';
	import CommonDialog from '@/component/common/dialog';
	import { camelCase, debounce } from 'lodash';
	import productTypeDescriptions from '@/content/productTypeDescriptions.js';
	import { ValidationObserver, ValidationProvider } from 'vee-validate';
	import { ElementTools, GoogleAnalytics } from '@/utils';
	import moment from 'moment';
	import CommonStructureSection from '@/component/common/structure/section';

	export default {
		name: 'app-calculator-max-ltv-tool',

		components: {
			CommonStructureSection,
			CommonDialog,
			ValidationObserver,
			ValidationProvider
		},

		data() {
			return {
				maxLtvToolActive: false,
				loading: true,
				applicationType: 'single',
				setApplicationType: null,
				age: 61,
				setAge: null,
				valuation: 450000,
				setValuation: null,
				data: null,
				cards: [],
				productDescription: {
					title: null,
					description: null
				},
				applicationTypes: [
					{ value: 'single', text: 'Single Application', icon: 'mdi-account' },
					{ value: 'joint', text: 'Joint Application', icon: 'mdi-account-multiple' }
				],
				productTypes: [
					{ name: 'voluntary', rateType: 'MER' },
					{ name: 'interest-serviced', rateType: 'MER' },
					{ name: 'mandatory', rateType: 'MER' },
					{ name: 'retirement', rateType: 'AER' }
				]
			};
		},

		computed: {
			ltv() {
				return (Math.round((this.setAge / 2) * 100) / 100).toFixed(2);
			},

			content() {
				return { productTypeDescriptions };
			}
		},

		created() {
			this.setApplicationType = this.applicationType;
			this.setAge = this.age;
			this.setValuation = this.valuation;
			this.fetchData();
		},

		methods: {
			...mapActions('CmsSite', ['getDbContent', 'getCacheContent']),

			/**
			 * @name open
			 * @description Open dialog
			 */
			open() {
				document.documentElement.classList.add('overflow-y-hidden');
				this.maxLtvToolActive = true;
				GoogleAnalytics.maxLtvOpened();
			},

			/**
			 * @name close
			 * @description Close dialog
			 */
			close() {
				this.maxLtvToolActive = false;
				this.$emit('closed');
			},

			/**
			 * @name fetchData
			 * @description TODO
			 */
			async fetchData() {
				this.loading = true;
				this.cards = [];
				const promises = [];

				this.productTypes.forEach((productType, index) => {
					const promise = this.getCacheContent(`ltv-by-age/${productType.name}/${this.applicationType}/${this.setAge}.json`).then((data) => {
						let smoothedPoints = this.getSmoothedPoints(data.data);
						let min = this.findMinRate(smoothedPoints);
						let max = this.findMaxLtv(smoothedPoints);
						let content = this.content?.productTypeDescriptions?.[camelCase(productType.name)];
						return {
							index: index,
							ref: productType.name,
							title: content.title,
							description: { title: content.title, description: content.description },
							points: smoothedPoints,
							rateType: productType.rateType,
							minRate: min.interestRate,
							maxRate: max.interestRate,
							maxLtv: max.loanValue,
							amount: this.calcLoan(max.loanValue),
							rate: max.interestRate
						};
					});
					promises.push(promise);
				});

				const results = await Promise.allSettled(promises);
				this.loading = false;
				results.forEach((result) => {
					if (result.status === 'fulfilled') this.cards.push(result.value);
				});

				if (!this.cards.length) {
					ElementTools.fireNotification(this.$el, 'error', 'Max LTV Tool currently unavailable');
					GoogleAnalytics.maxLtvError();
				} else this.cards = this.cards.sort((a, b) => a.index - b.index);
			},

			/**
			 * @name getSmoothedPoints
			 * @description TODO
			 */
			getSmoothedPoints(arr) {
				//Remove 0
				arr = arr.filter((item) => item.interestRate !== 0);
				//Remove entries older than 3 days
				arr = arr.filter((item) => {
					const timestamp = moment(item.created);
					const isWithin3Days = timestamp.isAfter(moment().subtract(3, 'days'));
					return isWithin3Days;
				});
				//Sort by highest loanValue
				arr.sort((a, b) => b.loanValue - a.loanValue);
				//Remove items if interestRate is greater than previous interestRate
				let minInterestRate = Infinity;
				arr = arr.filter((item) => {
					if (item.interestRate <= minInterestRate) {
						minInterestRate = item.interestRate;
						return true;
					}
					return false;
				});

				return arr;
			},

			/**
			 * @name findMinRate
			 * @description TODO
			 */
			findMinRate(arr) {
				return arr.reduce((min, item) => (item.interestRate < min.interestRate ? item : min));
			},

			/**
			 * @name findMaxLtv
			 * @description TODO
			 */
			findMaxLtv(arr) {
				return arr.reduce((max, item) => (item.loanValue > max.loanValue ? item : max));
			},

			/**
			 * @name createInterpolator
			 * @description TODO
			 */
			createInterpolator(points) {
				const sorted = [...points].sort((a, b) => a.x - b.x);

				return (x) => {
					if (x <= sorted[0].x) return sorted[0].y;
					if (x >= sorted[sorted.length - 1].x) return sorted[sorted.length - 1].y;

					for (let i = 0; i < sorted.length - 1; i++) {
						const p1 = sorted[i];
						const p2 = sorted[i + 1];

						if (x >= p1.x && x <= p2.x) {
							const t = (x - p1.x) / (p2.x - p1.x);
							const y = p1.y + t * (p2.y - p1.y);

							// round to 2 decimal places (number)
							return Math.round(y * 100) / 100;
						}
					}
				};
			},

			/**
			 * @name isValid
			 * @description TODO
			 */
			async isValid() {
				const isValid = await this.$refs['age_observer'].validate();
				return isValid;
			},

			/**
			 * @name openProductDescription
			 * @description Open dialog
			 */
			openProductDescription(product) {
				this.productDescription = product;
				this.$refs.productDescriptionDialog.open();
			},

			/**
			 * @name calcLoan
			 * @description TODO
			 */
			calcLoan(ltv) {
				return numeral(((this.setValuation / 100) * (ltv * 100)) / 100).format('0');
			},

			/**
			 * @name lifeTypeUpdate
			 * @description TODO
			 */
			lifeTypeUpdate: debounce(function() {
				this.setApplicationType = this.applicationType;
				this.setAge = this.age;
				this.setValuation = this.valuation;
				this.fetchData();
			}, 250),

			/**
			 * @name ageUpdate
			 * @description TODO
			 */
			ageUpdate: debounce(function() {
				this.setApplicationType = this.applicationType;
				this.setAge = this.age;
				this.setValuation = this.valuation;
				this.fetchData();
			}, 500),

			/**
			 * @name valuationUpdate
			 * @description TODO
			 */
			valuationUpdate: debounce(async function() {
				let isValid = await this.isValid();
				if (isValid) {
					this.loading = true;
					this.updateValuation();
				}
			}, 500),

			/**
			 * @name updateValuation
			 * @description TODO
			 */
			updateValuation: debounce(function() {
				this.setApplicationType = this.applicationType;
				this.setAge = this.age;
				this.setValuation = this.valuation;

				this.cards.forEach((x) => {
					x.amount = this.calcLoan(x.maxLtv);
					x.rate = x.maxRate;
				});

				this.loading = false;
			}, 500),

			/**
			 * @name ltvUpdate
			 * @description TODO
			 */
			ltvUpdate: debounce(function(card) {
				card.rate = undefined;
				this.updateLtv(card);
			}, 200),

			/**
			 * @name ltvUpdate
			 * @description TODO
			 */
			updateLtv: debounce(function(card) {
				let ltv = numeral((card.amount / this.setValuation) * 100).format('0.00');
				let points = this.renameParams(card.points);
				const interpolate = this.createInterpolator(points);
				card.rate = interpolate(ltv);
			}, 500),

			/**
			 * @name renameParams
			 * @description TODO
			 */
			renameParams(arr) {
				return arr.map(({ loanValue, interestRate, ...rest }) => ({
					x: loanValue,
					y: interestRate,
					...rest // preserves any other properties if they exist
				}));
			},

			/**
			 * @name openDisclaimer
			 * @description TODO
			 */
			openDisclaimer() {
				this.$refs.disclaimerDialog.open();
			}
		}
	};
</script>

<style lang="scss" scoped>
	#map {
		width: 100%;
		height: 500px;
	}
</style>
